import React, { useState, Component } from 'react';
import { ArrowLeftIcon, ShieldCheckIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { CartItem as CartItemComponent } from '../components/CartItem';
import { RDSummaryCard } from '../components/RDSummaryCard';
import { CartItem } from '../types';
import { mockProducts } from '../data/mockData';
interface CartProps {
  cart: CartItem[];
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemoveItem: (productId: string) => void;
  onSwapItem: (currentProductId: string, alternativeId: string) => void;
  onNavigateBack: () => void;
  onNavigateToCheckout: () => void;
  onNavigateToProfile: () => void;
}
export function Cart({
  cart,
  onUpdateQuantity,
  onRemoveItem,
  onSwapItem,
  onNavigateBack,
  onNavigateToCheckout,
  onNavigateToProfile
}: CartProps) {
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const STIPEND_TOTAL = 200;
  const TAX_RATE = 0.085;
  const coveredItems = cart.filter(item => item.isCovered);
  const extraItems = cart.filter(item => !item.isCovered);
  const coveredSubtotal = coveredItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const coveredTax = coveredSubtotal * TAX_RATE;
  const coveredTotal = coveredSubtotal + coveredTax;
  const stipendUsed = Math.min(coveredTotal, STIPEND_TOTAL);
  const planApprovedCount = cart.filter(item => item.product.rdRecommendation === 'recommended').length;
  const extraSubtotal = extraItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const extraTax = extraSubtotal * TAX_RATE;
  const userPays = extraSubtotal + extraTax;
  const lolaFee = coveredSubtotal > 0 ? coveredSubtotal * 0.05 : 0;
  return <div className="min-h-screen bg-gray-50" data-id="element-104">
      <Header cartCount={cartCount} onCartClick={() => {}} onProfileClick={onNavigateToProfile} data-id="element-105" />

      <div className="max-w-lg mx-auto px-4 py-6" data-id="element-106">
        <button onClick={onNavigateBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors" data-id="element-107">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-108" />
          <span className="font-medium" data-id="element-109">Continue Shopping</span>
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-110">Your Cart</h1>

        {cart.length === 0 ? <div className="text-center py-12" data-id="element-111">
            <p className="text-gray-500 mb-4" data-id="element-112">Your cart is empty</p>
            <button onClick={onNavigateBack} className="bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-6 rounded-full transition-colors" data-id="element-113">
              Start Shopping
            </button>
          </div> : <>
            <RDSummaryCard stipendUsed={stipendUsed} stipendTotal={STIPEND_TOTAL} planApprovedCount={planApprovedCount} totalCount={cart.length} data-id="element-114" />

            {coveredItems.length > 0 && <div className="mb-6" data-id="element-115">
                <div className="rounded-2xl p-3 mb-3 bg-green-50 border-2 border-green-200" data-id="element-116">
                  <div className="flex items-center justify-between" data-id="element-117">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-118">
                      <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm" data-id="element-119">
                        Covered by Your Plan
                      </span>
                    </h2>
                    <div className="flex items-center gap-2 text-xs text-green-700 bg-green-100 px-3 py-1 rounded-full" data-id="element-120">
                      <ShieldCheckIcon className="w-4 h-4" data-id="element-121" />
                      <span className="font-medium" data-id="element-122">RD Reviewed</span>
                    </div>
                  </div>
                  <p className="text-xs text-green-700 mt-2" data-id="element-123">
                    Healthcare plan covers these items (including tax)
                  </p>
                </div>
                <div className="space-y-3" data-id="element-124">
                  {coveredItems.map(item => <CartItemComponent key={item.product.id} item={item} onUpdateQuantity={onUpdateQuantity} onRemove={onRemoveItem} onSwap={onSwapItem} allProducts={mockProducts} data-id="element-125" />)}
                </div>
              </div>}

            {extraItems.length > 0 && <div className="mb-6" data-id="element-126">
                <div className="rounded-2xl p-3 mb-3 bg-blue-50 border-2 border-blue-200" data-id="element-127">
                  <div className="flex items-center justify-between" data-id="element-128">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-129">
                      <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm" data-id="element-130">
                        Extra Items
                      </span>
                    </h2>
                    <div className="text-xs text-blue-700 bg-blue-100 px-3 py-1 rounded-full" data-id="element-131">
                      <span className="font-medium" data-id="element-132">You pay</span>
                    </div>
                  </div>
                  <p className="text-xs text-blue-700 mt-2" data-id="element-133">
                    Items not covered by your plan
                  </p>
                </div>
                <div className="space-y-3" data-id="element-134">
                  {extraItems.map(item => <CartItemComponent key={item.product.id} item={item} onUpdateQuantity={onUpdateQuantity} onRemove={onRemoveItem} onSwap={onSwapItem} allProducts={mockProducts} data-id="element-135" />)}
                </div>
              </div>}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6" data-id="element-136">
              <div className="space-y-2 mb-3" data-id="element-137">
                <div className="flex justify-between text-sm" data-id="element-138">
                  <span className="text-green-600 font-medium" data-id="element-139">
                    Covered Items (subtotal)
                  </span>
                  <span className="font-medium text-green-600" data-id="element-140">
                    ${coveredSubtotal.toFixed(2)}
                  </span>
                </div>
                {coveredTax > 0 && <div className="flex justify-between text-sm" data-id="element-141">
                    <span className="text-green-600" data-id="element-142">Tax on Covered Items</span>
                    <span className="font-medium text-green-600" data-id="element-143">
                      ${coveredTax.toFixed(2)}
                    </span>
                  </div>}
                {coveredTotal > 0 && <div className="flex justify-between text-sm pb-2 border-b border-gray-200" data-id="element-144">
                    <span className="text-green-600 font-semibold" data-id="element-145">
                      Covered by Healthcare Plan
                    </span>
                    <span className="font-semibold text-green-600" data-id="element-146">
                      ${stipendUsed.toFixed(2)}
                    </span>
                  </div>}
                {lolaFee > 0 && <div className="flex justify-between text-sm text-gray-500" data-id="element-147">
                    <span data-id="element-148">Lola Fee (paid by plan)</span>
                    <span data-id="element-149">${lolaFee.toFixed(2)}</span>
                  </div>}
                {extraSubtotal > 0 && <>
                    <div className="flex justify-between text-sm pt-2" data-id="element-150">
                      <span className="text-gray-600" data-id="element-151">Extra Items</span>
                      <span className="font-medium text-gray-900" data-id="element-152">
                        ${extraSubtotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-153">
                      <span className="text-gray-600" data-id="element-154">Tax on Extra Items</span>
                      <span className="font-medium text-gray-900" data-id="element-155">
                        ${extraTax.toFixed(2)}
                      </span>
                    </div>
                  </>}
              </div>
              <div className="border-t border-gray-200 pt-3" data-id="element-156">
                <div className="flex justify-between" data-id="element-157">
                  <span className="font-bold text-gray-900" data-id="element-158">You Pay</span>
                  <span className="font-bold text-xl text-gray-900" data-id="element-159">
                    ${userPays.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <button onClick={onNavigateToCheckout} className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-full text-lg shadow-lg transition-colors" data-id="element-160">
              Proceed to Checkout
            </button>

            <p className="text-center text-xs text-gray-500 mt-3" data-id="element-161">
              Your healthcare plan covers up to $200/week for plan-approved
              items (including tax)
            </p>
          </>}
      </div>
    </div>;
}